/**
 * Seo — componente declarativo de metadados por página.
 *
 * Injeta title, description, canonical, robots, Open Graph, Twitter Cards e
 * blocos JSON-LD no <head> via react-helmet-async. As rotas públicas estáveis
 * têm essas tags "assadas" em HTML estático pelo prerender (scripts/prerender.mjs),
 * garantindo preview correto também em crawlers que não executam JavaScript.
 */

import { Helmet } from "react-helmet-async";
import { ORG, SITE_URL, DEFAULT_OG_IMAGE, absoluteUrl } from "@/config/seo";

export interface SeoProps {
  /** Título da aba/página. Sufixado com a marca, exceto na home. */
  title: string;
  description: string;
  /** Caminho canônico (ex.: "/eventos"). Default: home. */
  canonicalPath?: string;
  /** Imagem para og:image/twitter:image (caminho relativo ou URL absoluta). */
  image?: string;
  /** Tipo Open Graph. Default: "website". */
  type?: "website" | "article" | "profile";
  /** Quando true, emite robots noindex,nofollow (páginas privadas/admin). */
  noindex?: boolean;
  keywords?: string;
  /** Um ou mais objetos JSON-LD (schema.org). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Sobrescreve o título completo (sem sufixo de marca). */
  rawTitle?: boolean;
}

const Seo = ({
  title,
  description,
  canonicalPath = "/",
  image,
  type = "website",
  noindex = false,
  keywords,
  jsonLd,
  rawTitle = false,
}: SeoProps) => {
  const fullTitle = rawTitle ? title : `${title} | ${ORG.shortName}`;
  const canonical = absoluteUrl(canonicalPath);
  const ogImage = absoluteUrl(image || DEFAULT_OG_IMAGE);
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="pt-BR" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="pt_BR" />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={ORG.name} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={ORG.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
export { SITE_URL };
